// BOORO: the Tasks area, the ticket board as kanban or list beside the chats.
// It shows the tasks of every office of the person; the filter narrows it.

import { formatSavedMinutes, taskFromRequest } from "@booro/tasks";
import { itemInScope, topGroupOf } from "@booro/team";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
  OrgAgent,
  OrgSnapshot,
  TaskMetrics,
  TaskOutcome,
  TaskSummary,
  Ticket,
  TicketPriority,
} from "@rakazo/contracts";
import {
  isTicketCompletedStatus,
  TASK_RESULT_MAX_LENGTH,
  TICKET_DESCRIPTION_MAX_LENGTH,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  taskDisplayStatus,
  taskWorkingRunStatus,
} from "@rakazo/contracts";
import { formatCompactDuration } from "@rakazo/core";
import {
  BotAvatar,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  NativeSelect,
  NativeSelectOption,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@rakazo/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { spaceQueries, useQueryUserId } from "../../lib/query";
import { formatRelativeTime } from "../../lib/relative-time";
import type { RpcClient } from "../../lib/rpc";
import { rpc } from "../../lib/rpc";
import { useTaskDisplayStatusLabels } from "../../lib/task-display-status";
import { taskModelLabel } from "../../lib/task-model";
import { useTicketPriorityLabels } from "../../lib/ticket-priority";
import { useTicketStatusLabels } from "../../lib/ticket-status";
import { errorText } from "../../lib/user-error";
import type { BoardAgent } from "../Board";
import { TicketBoardColumns, TicketDialog } from "../Board";
import { AgentPicker } from "./agent-picker";
import { officeRpc, openAgentChat, ScopeFilter, useOrgScope, useOrgSnapshot } from "./org-scope";
import type { WorkspaceChrome } from "./workspace-header";
import { WorkspaceViewHeader } from "./workspace-header";

export type TasksLayout = "board" | "list";

const LAYOUT_STORAGE_KEY = "booro:tasks-layout";

export function readTasksLayout(): TasksLayout {
  try {
    return window.localStorage.getItem(LAYOUT_STORAGE_KEY) === "list" ? "list" : "board";
  } catch {
    return "board";
  }
}

function writeTasksLayout(layout: TasksLayout): void {
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
  } catch {
    // Private mode or blocked storage; the in-memory choice still applies.
  }
}

export type CompletionFilter = "today" | "week" | "month" | "all";
type PriorityFilter = "all" | TicketPriority;

const COMPLETION_STORAGE_KEY = "booro:tasks-completion";
const PRIORITY_STORAGE_KEY = "booro:tasks-priority";
const COMPLETION_FILTERS: readonly CompletionFilter[] = ["today", "week", "month", "all"];

function readCompletionFilter(): CompletionFilter {
  try {
    const value = window.localStorage.getItem(COMPLETION_STORAGE_KEY);
    return COMPLETION_FILTERS.includes(value as CompletionFilter)
      ? (value as CompletionFilter)
      : "week";
  } catch {
    return "week";
  }
}

function writeCompletionFilter(value: CompletionFilter): void {
  try {
    window.localStorage.setItem(COMPLETION_STORAGE_KEY, value);
  } catch {
    // Private mode or blocked storage; the in-memory choice still applies.
  }
}

function readPriorityFilter(): PriorityFilter {
  try {
    const value = window.localStorage.getItem(PRIORITY_STORAGE_KEY);
    if (value === "all") return "all";
    return TICKET_PRIORITIES.includes(value as TicketPriority) ? (value as TicketPriority) : "all";
  } catch {
    return "all";
  }
}

function writePriorityFilter(value: PriorityFilter): void {
  try {
    window.localStorage.setItem(PRIORITY_STORAGE_KEY, value);
  } catch {
    // Private mode or blocked storage; the in-memory choice still applies.
  }
}

/** The local start of a completed-task period; open tasks are never filtered. */
export function completionRangeStart(range: CompletionFilter, now: Date = new Date()): Date | null {
  if (range === "all") return null;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === "week") {
    const day = start.getDay();
    start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
  } else if (range === "month") {
    start.setDate(1);
  }
  return start;
}

/** Whether a task is open, or was finished inside the selected period. */
export function taskInCompletionRange(
  ticket: Ticket,
  range: CompletionFilter,
  now: Date = new Date(),
): boolean {
  if (range === "all" || !isTicketCompletedStatus(ticket.status)) return true;
  const start = completionRangeStart(range, now);
  if (!start) return true;
  const completed = Date.parse(ticket.completedAt ?? ticket.updatedAt);
  return Number.isFinite(completed) && completed >= start.getTime();
}

/** Open tasks first, then the most recently changed. */
export function sortTasksForList(tickets: readonly Ticket[]): Ticket[] {
  return [...tickets].sort((a, b) => {
    const aDone = isTicketCompletedStatus(a.status);
    const bDone = isTicketCompletedStatus(b.status);
    if (aDone !== bDone) return aDone ? 1 : -1;
    if (a.updatedAt !== b.updatedAt) return a.updatedAt < b.updatedAt ? 1 : -1;
    return a.id < b.id ? 1 : -1;
  });
}

/** How often the board reloads when no live event came. */
const BOARD_REFRESH_MS = 20_000;

/** How many finished tasks the list shows before the person asks for more. */
const COMPLETED_LIST_LIMIT = 20;

/**
 * The tasks of every office of the person. Each office reports its own
 * changes, so the board listens to all of them.
 */
function useBoardTasks(officeIds: readonly string[]) {
  const [tasks, setTasks] = useState<{ ticket: Ticket; summary: TaskSummary }[]>([]);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    try {
      setTasks((await rpc.booroTasks.board()).tasks);
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
  }, []);
  const officeKey = officeIds.join(",");
  const userId = useQueryUserId();
  const queryClient = useQueryClient();

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), BOARD_REFRESH_MS);
    let active = true;
    const controllers: AbortController[] = [];
    for (const spaceId of officeKey ? officeKey.split(",") : []) {
      void (async () => {
        while (active) {
          const controller = new AbortController();
          controllers.push(controller);
          try {
            const stream = await officeRpc(spaceId).boards.subscribe(undefined, {
              signal: controller.signal,
            });
            for await (const event of stream) {
              if (!active) return;
              // An open task's comments are read again when its office reports a change.
              const comments = spaceQueries(userId, spaceId).tickets.comments;
              void queryClient.invalidateQueries({
                queryKey: event.ticketId
                  ? comments.queryKey({ input: { ticketId: event.ticketId } })
                  : comments.key(),
              });
              await reload();
            }
          } catch {
            // The stream ends on navigation or a dropped connection; retry below.
          }
          if (!active) return;
          await new Promise((resolve) => setTimeout(resolve, 1_000));
        }
      })();
    }
    return () => {
      active = false;
      window.clearInterval(timer);
      for (const controller of controllers) controller.abort();
    };
  }, [officeKey, reload, userId, queryClient]);

  return { tasks, error, reload };
}

/** The top group of the agent, or its office, as a short label on a card. */
function placeLabel(snapshot: OrgSnapshot, ticket: Ticket): string {
  const agent = snapshot.agents.find((candidate) => candidate.id === ticket.assigneeBotId);
  const group = agent ? topGroupOf(snapshot.groups, agent.groupId) : null;
  if (group) return group.name;
  return snapshot.offices.find((office) => office.id === ticket.spaceId)?.name ?? "";
}

export function TasksView({ title, chrome }: { title: string; chrome: WorkspaceChrome }) {
  const { t } = useLingui();
  const { snapshot, error: orgError } = useOrgSnapshot();
  const [scope, setScope] = useOrgScope();
  const officeIds = useMemo(
    () => snapshot?.offices.map((office) => office.id) ?? [],
    [snapshot?.offices],
  );
  const { tasks, error: boardError, reload } = useBoardTasks(officeIds);
  const [layout, setLayout] = useState<TasksLayout>(readTasksLayout);
  const [completion, setCompletion] = useState<CompletionFilter>(readCompletionFilter);
  const [priority, setPriority] = useState<PriorityFilter>(readPriorityFilter);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | "new" | null>(null);
  const completionFilterId = useId();
  const priorities = useTicketPriorityLabels();
  const error = boardError || orgError;

  const agents: OrgAgent[] = snapshot?.agents ?? [];
  const shown = useMemo(() => {
    if (!snapshot) return [];
    return tasks.filter(({ ticket }) =>
      itemInScope(
        snapshot.groups,
        snapshot.agents,
        { spaceId: ticket.spaceId, botId: ticket.assigneeBotId },
        scope,
      ),
    );
  }, [tasks, snapshot, scope]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    const now = new Date();
    return shown.filter(({ ticket, summary }) => {
      if (priority !== "all" && ticket.priority !== priority) return false;
      if (!taskInCompletionRange(ticket, completion, now)) return false;
      if (!needle) return true;
      return [ticket.ref, ticket.title, ticket.description ?? "", summary.result ?? ""]
        .join("\n")
        .toLocaleLowerCase()
        .includes(needle);
    });
  }, [shown, completion, priority, query]);
  const tickets = useMemo(() => filtered.map((task) => task.ticket), [filtered]);
  const summaries = useMemo(
    () => new Map(filtered.map((task) => [task.ticket.id, task.summary])),
    [filtered],
  );
  // The agents a new task may go to: those in the filter, or all of them.
  const assignable = useMemo(
    () =>
      snapshot
        ? agents.filter((agent) =>
            itemInScope(
              snapshot.groups,
              agents,
              { spaceId: agent.spaceId, botId: agent.id },
              scope,
            ),
          )
        : [],
    [snapshot, agents, scope],
  );
  const severalPlaces = (snapshot?.offices.length ?? 0) > 1 || (snapshot?.groups.length ?? 0) > 0;
  const selected =
    selectedId && selectedId !== "new"
      ? (tasks.find((task) => task.ticket.id === selectedId)?.ticket ?? null)
      : null;
  const completionLabels: Record<CompletionFilter, string> = {
    today: t`Today`,
    week: t`This week`,
    month: t`This month`,
    all: t`All time`,
  };
  const emptyLabel = shown.length > 0 ? t`No tasks match these filters` : undefined;

  return (
    <>
      <WorkspaceViewHeader
        title={title}
        chrome={chrome}
        actions={
          <>
            <Tabs
              value={layout}
              onValueChange={(value) => {
                const next = value === "list" ? "list" : "board";
                setLayout(next);
                writeTasksLayout(next);
              }}
            >
              <TabsList aria-label={t`Layout`}>
                <TabsTrigger value="board">
                  <Trans>Board</Trans>
                </TabsTrigger>
                <TabsTrigger value="list">
                  <Trans>List</Trans>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              size="sm"
              onClick={() => setSelectedId("new")}
              disabled={assignable.length === 0}
            >
              <Trans>New task</Trans>
            </Button>
          </>
        }
      />
      {snapshot ? (
        <div
          data-testid="task-filters"
          className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2"
        >
          <Input
            type="search"
            data-testid="task-search"
            aria-label={t`Search tasks`}
            placeholder={t`Search tasks`}
            value={query}
            className="h-8 w-40 min-w-0 sm:w-56"
            onChange={(event) => setQuery(event.target.value)}
          />
          {snapshot.offices.length > 1 || snapshot.agents.length > 1 ? (
            <ScopeFilter snapshot={snapshot} scope={scope} onChange={setScope} />
          ) : null}
          <label
            htmlFor={completionFilterId}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <span>
              <Trans>Done</Trans>
            </span>
            <NativeSelect
              id={completionFilterId}
              data-testid="task-completion-filter"
              aria-label={t`Done`}
              value={completion}
              onChange={(event) => {
                const next = event.target.value as CompletionFilter;
                setCompletion(next);
                writeCompletionFilter(next);
              }}
            >
              {COMPLETION_FILTERS.map((value) => (
                <NativeSelectOption key={value} value={value}>
                  {completionLabels[value]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </label>
          <NativeSelect
            data-testid="task-priority-filter"
            aria-label={t`Priority`}
            value={priority}
            onChange={(event) => {
              const next = event.target.value as PriorityFilter;
              setPriority(next);
              writePriorityFilter(next);
            }}
          >
            <NativeSelectOption value="all">{t`All priorities`}</NativeSelectOption>
            {TICKET_PRIORITIES.map((value) => (
              <NativeSelectOption key={value} value={value}>
                {priorities[value]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="px-4 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div
        className={`relative min-h-0 min-w-0 flex-1 px-4 pt-4 ${
          layout === "board" ? "overflow-hidden pb-4" : "rk-scroll overflow-auto pb-24"
        }`}
      >
        {layout === "board" ? (
          <TicketBoardColumns
            tickets={tickets}
            bots={agents}
            error={error}
            emptyLabel={emptyLabel}
            onSelect={(ticket) => setSelectedId(ticket.id)}
            cardExtra={(ticket) => (
              <>
                <TaskResult result={summaries.get(ticket.id)?.result} />
                {snapshot && severalPlaces ? (
                  <PlaceBadge label={placeLabel(snapshot, ticket)} />
                ) : null}
              </>
            )}
            // BOORO: the avatar of the agent is alive while it works on this task.
            cardBotStatus={(ticket) =>
              taskWorkingRunStatus(summaries.get(ticket.id)?.runStatus) ?? undefined
            }
            cardMenu={(ticket) => (
              <TaskCardMenu ticket={ticket} summary={summaries.get(ticket.id)} onChanged={reload} />
            )}
          />
        ) : (
          <TaskList
            tickets={tickets}
            bots={agents}
            summaries={summaries}
            emptyLabel={emptyLabel}
            placeOf={
              snapshot && severalPlaces ? (ticket) => placeLabel(snapshot, ticket) : undefined
            }
            onSelect={(ticket) => setSelectedId(ticket.id)}
            onStopped={reload}
          />
        )}
      </div>
      {selectedId === "new" ? (
        <NewTaskDialog
          agents={assignable}
          offices={snapshot?.offices ?? []}
          onClose={() => setSelectedId(null)}
          onCreated={reload}
        />
      ) : selected ? (
        <TicketDialog
          key={selected.id}
          ticket={selected}
          // A task stays in its office, so only agents of that office can take it over.
          bots={agents.filter((agent) => agent.spaceId === selected.spaceId)}
          spaceId={selected.spaceId}
          extra={
            <>
              {/* BOORO: what the task was and what it cost, before the form. */}
              <TaskFacts
                ticket={selected}
                bot={agents.find((candidate) => candidate.id === selected.assigneeBotId)}
                runStatus={summaries.get(selected.id)?.runStatus}
                client={officeRpc(selected.spaceId)}
              />
              <TaskResultField
                ticketId={selected.id}
                result={summaries.get(selected.id)?.result ?? null}
                client={officeRpc(selected.spaceId)}
                onSaved={reload}
              />
              {selected.assigneeBotId ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  onClick={() =>
                    openAgentChat(
                      selected.spaceId,
                      `/app/${selected.assigneeBotId}?task=${encodeURIComponent(selected.id)}`,
                    )
                  }
                >
                  <Trans>Open in chat</Trans>
                </Button>
              ) : null}
            </>
          }
          onClose={() => setSelectedId(null)}
          onSaved={reload}
        />
      ) : null}
    </>
  );
}

/** Where a task belongs, when the board shows more than one group or office. */
function PlaceBadge({ label }: { label: string }) {
  if (!label) return null;
  return (
    <span className="max-w-full truncate rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
      {label}
    </span>
  );
}

/**
 * The facts of a task: who worked on it, when it was created and finished, what
 * it cost and how much time it saved. A finished task reads like a record.
 */
export function TaskFacts({
  ticket,
  bot,
  runStatus,
  client = rpc,
}: {
  ticket: Ticket;
  bot: BoardAgent | undefined;
  /** The status of the run that works on the task. */
  runStatus: string | null | undefined;
  /** The office of the task. */
  client?: RpcClient;
}) {
  const { t, i18n } = useLingui();
  const outcomes = useTaskOutcomeLabels();
  const statuses = useTaskDisplayStatusLabels();
  const [metrics, setMetrics] = useState<TaskMetrics | null>(null);

  useEffect(() => {
    let active = true;
    void client.booroTasks
      .get({ ticketId: ticket.id })
      .then((detail) => {
        if (active) setMetrics(detail.metrics);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [ticket.id, client]);

  const locale = i18n.locale || "de";
  const facts: string[] = [];
  if (metrics) {
    if (metrics.outcome !== "open") facts.push(outcomes[metrics.outcome]);
    if (metrics.workMs > 0) {
      const duration = formatCompactDuration(metrics.workMs);
      if (duration) facts.push(duration);
    }
    // Costs without a price stay unknown, they do not become zero.
    facts.push(
      metrics.costUsd === null
        ? t`Cost unknown`
        : t`Cost: ${formatTaskCost(metrics.costUsd, locale)}`,
    );
    if (metrics.minutesSaved !== null) {
      facts.push(t`Saved ${formatSavedMinutes(metrics.minutesSaved)}`);
    }
    const model = taskModelLabel(metrics.modelProvider, metrics.modelId);
    if (model) facts.push(t`Model: ${model}`);
  }

  return (
    <div data-testid="task-facts" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
        {bot ? (
          <span className="flex items-center gap-1.5">
            <BotAvatar
              color={bot.color}
              identity={bot.id}
              size={20}
              status={taskWorkingRunStatus(runStatus) ?? undefined}
            />
            <span className="truncate text-foreground/85" dir="auto">
              {bot.name}
            </span>
          </span>
        ) : null}
        <span>{statuses[taskDisplayStatus(ticket.status, null)]}</span>
        <span>
          <Trans>Created</Trans> {formatRelativeTime(ticket.createdAt)}
        </span>
        {ticket.completedAt ? (
          <span>
            <Trans>Finished</Trans> {formatRelativeTime(ticket.completedAt)}
          </span>
        ) : null}
      </div>
      {facts.length > 0 ? (
        <p className="text-[12.5px] text-muted-foreground">{facts.join(" · ")}</p>
      ) : null}
    </div>
  );
}

function TaskResult({ result }: { result: string | null | undefined }) {
  if (!result) return null;
  return (
    <span className="line-clamp-2 text-xs text-foreground/80" dir="auto">
      {result}
    </span>
  );
}

/**
 * Stops the agent that works on the task. The person sees the work on the
 * card, so the stop belongs there too.
 */
function StopTaskButton({
  botId,
  client,
  onStopped,
}: {
  botId: string;
  client: RpcClient;
  onStopped: () => Promise<void>;
}) {
  const { t } = useLingui();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <span className="flex flex-col items-end gap-1">
      <Button
        size="xs"
        variant="outline"
        aria-label={t`Stop the agent`}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await client.threads.stop({ botId });
            await onStopped();
          } catch (cause) {
            setError(errorText(cause, t`Could not stop the agent`));
          } finally {
            setBusy(false);
          }
        }}
      >
        {t`Stop`}
      </Button>
      {error ? (
        <span role="alert" className="text-[11px] text-destructive">
          {error}
        </span>
      ) : null}
    </span>
  );
}

/** The task's quick actions: move it, open its chat, or stop the agent. */
function TaskCardMenu({
  ticket,
  summary,
  onChanged,
}: {
  ticket: Ticket;
  summary: TaskSummary | undefined;
  onChanged: () => Promise<void>;
}) {
  const { t } = useLingui();
  const statuses = useTicketStatusLabels();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const client = officeRpc(ticket.spaceId);
  const working = taskWorkingRunStatus(summary?.runStatus);

  async function move(status: Ticket["status"]) {
    setBusy(true);
    setError("");
    try {
      await client.tickets.update({ id: ticket.id, status });
      await onChanged();
      setOpen(false);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    if (!ticket.assigneeBotId) return;
    setBusy(true);
    setError("");
    try {
      await client.threads.stop({ botId: ticket.assigneeBotId });
      await onChanged();
      setOpen(false);
    } catch (cause) {
      setError(errorText(cause, t`Could not stop the agent`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (next) setError("");
        setOpen(next);
      }}
    >
      <DropdownMenuTrigger
        data-testid={`task-menu-${ticket.id}`}
        aria-label={t`Task actions`}
        disabled={busy}
        render={<Button variant="ghost" size="icon-xs" className="text-muted-foreground" />}
      >
        <MoreHorizontal />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid={`task-menu-content-${ticket.id}`}
        align="end"
        className="w-auto min-w-44"
      >
        {ticket.assigneeBotId ? (
          <DropdownMenuItem
            onClick={() =>
              openAgentChat(
                ticket.spaceId,
                `/app/${ticket.assigneeBotId}?task=${encodeURIComponent(ticket.id)}`,
              )
            }
          >
            <Trans>Open in chat</Trans>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            <Trans>Move to</Trans>
          </DropdownMenuLabel>
          {TICKET_STATUSES.filter((status) => status !== ticket.status).map((status) => (
            <DropdownMenuItem
              key={status}
              closeOnClick={false}
              disabled={busy}
              onClick={() => void move(status)}
            >
              {statuses[status]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        {working && ticket.assigneeBotId ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem closeOnClick={false} disabled={busy} onClick={() => void stop()}>
              <Trans>Stop the agent</Trans>
            </DropdownMenuItem>
          </>
        ) : null}
        {error ? (
          <p role="alert" className="px-1.5 py-1 text-xs text-destructive">
            {error}
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TaskResultField({
  ticketId,
  result,
  client,
  onSaved,
}: {
  ticketId: string;
  result: string | null;
  client: RpcClient;
  onSaved: () => Promise<void>;
}) {
  const { t } = useLingui();
  const id = useId();
  const [value, setValue] = useState(result ?? "");
  const [error, setError] = useState("");

  async function save() {
    if (value.trim() === (result ?? "")) return;
    setError("");
    try {
      await client.booroTasks.setMeta({ ticketId, result: value });
      await onSaved();
    } catch (cause) {
      setError(errorText(cause));
    }
  }

  return (
    <label htmlFor={id} className="block space-y-1 text-sm">
      <span>
        <Trans>Result</Trans>
      </span>
      <Input
        id={id}
        data-testid="task-result"
        value={value}
        maxLength={TASK_RESULT_MAX_LENGTH}
        placeholder={t`What came out`}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => void save()}
      />
      {error ? (
        <span role="alert" className="block text-destructive">
          {error}
        </span>
      ) : null}
    </label>
  );
}

function TaskList({
  tickets,
  bots,
  summaries,
  emptyLabel,
  placeOf,
  onSelect,
  onStopped,
}: {
  tickets: readonly Ticket[];
  bots: readonly BoardAgent[];
  summaries: Map<string, TaskSummary>;
  /** Explains an empty list when filters hide the tasks. */
  emptyLabel?: string;
  /** Where a task belongs, when there is more than one place. */
  placeOf?: (ticket: Ticket) => string;
  onSelect: (ticket: Ticket) => void;
  onStopped: () => Promise<void>;
}) {
  const statuses = useTaskDisplayStatusLabels();
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const sorted = sortTasksForList(tickets);
  const open = sorted.filter((ticket) => !isTicketCompletedStatus(ticket.status));
  const completed = sorted.filter((ticket) => isTicketCompletedStatus(ticket.status));
  const visible = showAllCompleted
    ? sorted
    : [...open, ...completed.slice(0, COMPLETED_LIST_LIMIT)];

  if (tickets.length === 0) {
    return (
      <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {emptyLabel ?? <Trans>No tickets yet</Trans>}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <ul data-testid="task-list" className="divide-y divide-border">
        {visible.map((ticket) => {
          const bot = bots.find((candidate) => candidate.id === ticket.assigneeBotId);
          const summary = summaries.get(ticket.id);
          const working = taskWorkingRunStatus(summary?.runStatus);
          const stop =
            bot && working ? (
              <StopTaskButton
                botId={bot.id}
                client={officeRpc(ticket.spaceId)}
                onStopped={onStopped}
              />
            ) : null;
          return (
            <li key={ticket.id} className="relative">
              <button
                type="button"
                data-testid={`task-row-${ticket.id}`}
                onClick={() => onSelect(ticket)}
                className={`flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-accent ${
                  stop ? "pe-24" : ""
                }`}
              >
                <span className="w-16 shrink-0 text-xs text-muted-foreground">{ticket.ref}</span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm" dir="auto">
                    {ticket.title}
                  </span>
                  {summary?.result ? (
                    <span className="truncate text-xs text-muted-foreground" dir="auto">
                      {summary.result}
                    </span>
                  ) : null}
                </span>
                {placeOf ? (
                  <span className="hidden w-32 shrink-0 truncate text-xs text-muted-foreground lg:block">
                    {placeOf(ticket)}
                  </span>
                ) : null}
                {bot ? (
                  <span className="hidden w-36 shrink-0 items-center gap-2 sm:flex">
                    <BotAvatar
                      color={bot.color}
                      identity={bot.id}
                      size={20}
                      // BOORO: the avatar is alive while the agent works on this task.
                      status={taskWorkingRunStatus(summary?.runStatus) ?? undefined}
                    />
                    <span className="truncate text-xs text-muted-foreground">{bot.name}</span>
                  </span>
                ) : null}
                <span className="w-28 shrink-0 text-xs text-muted-foreground">
                  {summary ? statuses[summary.displayStatus] : null}
                </span>
                <time
                  dateTime={ticket.updatedAt}
                  className="hidden w-20 shrink-0 text-right text-xs text-muted-foreground md:block"
                >
                  {formatRelativeTime(ticket.updatedAt)}
                </time>
              </button>
              {stop ? (
                <span className="absolute end-2 top-1/2 -translate-y-1/2">{stop}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {completed.length > COMPLETED_LIST_LIMIT ? (
        <div className="flex justify-center pt-1">
          <Button
            data-testid="task-list-show-all"
            variant="ghost"
            size="sm"
            onClick={() => setShowAllCompleted((previous) => !previous)}
          >
            {showAllCompleted ? (
              <Trans>Show less</Trans>
            ) : (
              <>
                <Trans>Show all</Trans> {completed.length}
              </>
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** "$0.0123" for small sums, "$1.20" from one dollar up. */
export function formatTaskCost(costUsd: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: costUsd < 1 ? 4 : 2,
  }).format(costUsd);
}

function useTaskOutcomeLabels(): Record<Exclude<TaskOutcome, "open">, string> {
  const { t } = useLingui();
  return {
    success: t`Succeeded`,
    reworked: t`Reworked`,
    failed: t`Failed`,
    dropped: t`Dropped`,
  };
}

const LAST_AGENT_STORAGE_KEY = "booro:tasks-last-agent";

function readLastAgent(): string | null {
  try {
    return window.localStorage.getItem(LAST_AGENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLastAgent(botId: string): void {
  try {
    window.localStorage.setItem(LAST_AGENT_STORAGE_KEY, botId);
  } catch {
    // Private mode or blocked storage; the default agent is used next time.
  }
}

/** A task is just what should be done; the title comes from the request. */
function NewTaskDialog({
  agents,
  offices,
  onClose,
  onCreated,
}: {
  agents: readonly OrgAgent[];
  offices: readonly { id: string; name: string }[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const { t } = useLingui();
  const bots = agents;
  const [request, setRequest] = useState("");
  const [owner, setOwner] = useState(() => {
    const last = readLastAgent();
    return bots.find((bot) => bot.id === last)?.id ?? bots[0]?.id ?? "";
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ready = request.trim().length > 0 && Boolean(owner) && !busy;

  async function create() {
    if (!ready) return;
    setBusy(true);
    setError("");
    try {
      // The task is created in the office of its agent; nobody picks an office.
      const agent = agents.find((candidate) => candidate.id === owner);
      if (!agent) return;
      await officeRpc(agent.spaceId).tickets.create({
        ...taskFromRequest(request.slice(0, TICKET_DESCRIPTION_MAX_LENGTH)),
        assigneeBotId: owner,
        priority: "normal",
      });
      writeLastAgent(owner);
      await onCreated();
      onClose();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>New task</Trans>
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <Textarea
            autoFocus
            data-testid="new-task-request"
            aria-label={t`What should be done?`}
            placeholder={t`What should be done?`}
            rows={4}
            value={request}
            maxLength={TICKET_DESCRIPTION_MAX_LENGTH}
            onChange={(event) => setRequest(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void create();
              }
            }}
          />
          <div className="flex items-center justify-end gap-2">
            {bots.length > 1 ? (
              <AgentPicker
                aria-label={t`Agent`}
                className="me-auto"
                bots={bots.map((bot) => ({
                  ...bot,
                  hint:
                    offices.length > 1
                      ? offices.find((office) => office.id === bot.spaceId)?.name
                      : undefined,
                }))}
                value={owner}
                onChange={setOwner}
              />
            ) : null}
            <Button type="submit" disabled={!ready}>
              <Trans>Create</Trans>
            </Button>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
